import crudApi from "@serie3/common/api/crudApi";
import { Form } from "react-bootstrap";
import { useState, useRef, Fragment } from "react";
import ReferencedInputAndSubmit from "../layout/Form/Fields/ReferencedInputAndSubmit";
import { useParams } from "react-router";
import ProjectConfig from "@serie3/freelancesplaceapi/Config/Projects/ProjectConfig";
import ProjectCommentTimelineItem from "../LoggedIn/modules/projects/Projects/content/ProjectCommentTimelineItem";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import {
  faLock,
  faDiagramProject,
  faHourglassStart,
  faHourglassEnd,
  faClock,
  faCheckCircle,
  faFire,
  faComment,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FreelancerserplaceNavbar from "../signup/SubComponents/FreelancerserplaceNavbar";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const spin = `@keyframes spin { to { transform: rotate(360deg); } }`;

const statusColors = {
  Created:     { color: "#1a73e8", bg: "#e8f0fe", label: "Todo" },
  In_Progress: { color: "#00acc1", bg: "#e0f7fa", label: "In Progress" },
  On_Hold:     { color: "#9e9e9e", bg: "#f5f5f5", label: "On Hold" },
  Done:        { color: "#2e7d32", bg: "#e8f5e9", label: "Done" },
  Canceled:    { color: "#e53935", bg: "#ffebee", label: "Canceled" },
};

const priorityColors = {
  Highest: "#b71c1c",
  High:    "#e65100",
  Medium:  "#1a73e8",
  Low:     "#9e9e9e",
  Lowest:  "#bdbdbd",
};

const MetricCard = ({ icon, label, value, color = "#1a73e8", bg = "#e8f0fe" }) => (
  <div style={{
    flex: 1, minWidth: 0,
    background: "#fff",
    borderRadius: "12px",
    padding: "16px 18px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    display: "flex", alignItems: "center", gap: "14px",
  }}>
    <div style={{
      width: "40px", height: "40px", borderRadius: "10px",
      background: bg, color,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: "1rem", flexShrink: 0,
    }}>
      <FontAwesomeIcon icon={icon} />
    </div>
    <div>
      <div style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9e9e9e" }}>{label}</div>
      <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#212121", marginTop: "2px" }}>{value}</div>
    </div>
  </div>
);

const TicketPill = ({ action }) => {
  const s = statusColors[action.status] || statusColors.Created;
  const pColor = priorityColors[action.priority] || "#9e9e9e";
  return (
    <div style={{
      padding: "8px 10px",
      borderRadius: "8px",
      marginBottom: "5px",
      background: "#fafafa",
      border: "1px solid #f0f0f0",
    }}>
      <div style={{
        fontWeight: 600, fontSize: "0.82rem", color: "#212121",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {action.title}
      </div>
      {action.priority && (
        <div style={{ marginTop: "4px", display: "flex", gap: "5px", alignItems: "center" }}>
          <span style={{
            width: "6px", height: "6px", borderRadius: "50%",
            background: pColor, display: "inline-block", flexShrink: 0,
          }} />
          <span style={{ fontSize: "0.65rem", color: "#9e9e9e" }}>{action.priority}</span>
        </div>
      )}
    </div>
  );
};

const TicketColumn = ({ label, actions, status }) => {
  const s = statusColors[status] || statusColors.Created;
  return (
    <div style={{
      flex: 1, minWidth: 0,
      background: "#f8f9fb",
      borderRadius: "10px",
      overflow: "hidden",
    }}>
      <div style={{
        padding: "10px 12px",
        borderBottom: "1px solid #f0f0f0",
        display: "flex", alignItems: "center", gap: "8px",
      }}>
        <span style={{
          width: "8px", height: "8px", borderRadius: "50%",
          background: s.color, display: "inline-block", flexShrink: 0,
        }} />
        <span style={{ fontWeight: 700, fontSize: "0.78rem", color: "#424242" }}>{label}</span>
        <span style={{
          marginLeft: "auto",
          background: s.bg, color: s.color,
          borderRadius: "20px", fontSize: "0.68rem", fontWeight: 700,
          padding: "1px 8px",
        }}>{actions.length}</span>
      </div>
      <div style={{ padding: "8px", maxHeight: "320px", overflowY: "auto" }}>
        {actions.length === 0
          ? <div style={{ textAlign: "center", padding: "20px 0", color: "#bdbdbd", fontSize: "0.75rem" }}>Empty</div>
          : actions.map((a, i) => <TicketPill key={i} action={a} />)
        }
      </div>
    </div>
  );
};

const CustomerDashboard = ({ setToken }) => {
  const [showPasswordRequest, setShowPasswordRequest] = useState(true);
  const [onHold, setOnHold]       = useState([]);
  const [inProgress, setInProgress] = useState([]);
  const [todo, setTodo]           = useState([]);
  const [done, setDone]           = useState([]);
  const [canceled, setCanceled]   = useState([]);
  const [timePercentage, setTimePercentage] = useState(0);
  const [dashboardData, setDashboardData] = useState(null);
  const [showError, setShowError] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [workingHours, setWorkingHours] = useState([]);
  const [donePercentage, setDonePercentage] = useState(0);
  const [loading, setLoading] = useState(false);
  const passwordref = useRef(null);
  const { publicId } = useParams();

  const submitPassword = async () => {
    setLoading(true);
    const body = { password: passwordref.current.value };
    const response = await crudApi.search(
      ProjectConfig.endpoints.publicDashboard + publicId,
      body,
      true
    );
    setLoading(false);
    if (response.status === 200) {
      const tmpTimePercentage = (response.data.total_time / response.data.max_hours) * 100;
      setShowError(false);
      setShowPasswordRequest(false);
      setDataLoaded(true);
      setDashboardData(response.data);
      sortTickets(response.data.actions);
      setWorkingHours(response.data.tasks);
      setTimePercentage(isFinite(tmpTimePercentage) ? tmpTimePercentage : 0);
    } else {
      setShowError(true);
    }
  };

  const sortTickets = (actions = []) => {
    const buckets = { Created: [], In_Progress: [], Done: [], On_Hold: [], Canceled: [] };
    actions.forEach((a) => {
      const key = buckets[a.status] !== undefined ? a.status : "Created";
      buckets[key].push(a);
    });
    setTodo(buckets.Created);
    setInProgress(buckets.In_Progress);
    setDone(buckets.Done);
    setOnHold(buckets.On_Hold);
    setCanceled(buckets.Canceled);
    const total = actions.length;
    setDonePercentage(total ? Math.round(((buckets.Done.length + buckets.Canceled.length) / total) * 100) : 0);
  };

  const total = todo.length + inProgress.length + done.length + onHold.length + canceled.length;

  const doughnutData = total > 0 ? {
    labels: ["Todo", "In Progress", "On Hold", "Done", "Canceled"],
    datasets: [{
      data: [todo.length, inProgress.length, onHold.length, done.length, canceled.length],
      backgroundColor: ["#1a73e8", "#00acc1", "#9e9e9e", "#2e7d32", "#e53935"],
      borderColor: "#fff",
      borderWidth: 2,
      hoverOffset: 4,
    }],
  } : null;

  const barData = workingHours?.length > 0 ? {
    labels: workingHours.map((t) => t.task?.title || t.title || ""),
    datasets: [{
      label: "Hours",
      data: workingHours.map((t) => ((t.task?.total_time || t.total_time || 0) / 60).toFixed(1)),
      backgroundColor: "#1a73e8",
      borderRadius: 6,
    }],
  } : null;

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { color: "#f0f0f0" } },
    },
  };

  const onTrack = donePercentage >= timePercentage;
  const healthColor = onTrack ? "#2e7d32" : "#c62828";
  const healthBg    = onTrack ? "#e8f5e9"  : "#ffebee";

  const urgentOpen = [...todo, ...inProgress, ...onHold].filter(
    (a) => ["Highest", "High"].includes(a.priority)
  ).length;

  // ── Password screen ──────────────────────────────────────────────
  if (showPasswordRequest) {
    return (
      <>
        <style>{spin}</style>
        <FreelancerserplaceNavbar showLinks={false} showButtons={false} />
        <div style={{
          minHeight: "calc(100vh - 80px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "#f5f6fa", padding: "24px",
        }}>
          <div style={{
            background: "#fff", borderRadius: "16px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
            padding: "40px 36px", width: "100%", maxWidth: "400px",
            textAlign: "center",
          }}>
            <div style={{
              width: "56px", height: "56px", borderRadius: "14px",
              background: "linear-gradient(135deg, #1a73e8 0%, #1a237e 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px",
              boxShadow: "0 4px 14px rgba(26,115,232,0.3)",
            }}>
              <FontAwesomeIcon icon={faLock} style={{ color: "#fff", fontSize: "1.3rem" }} />
            </div>
            <div style={{ fontWeight: 800, fontSize: "1.25rem", color: "#212121", marginBottom: "6px" }}>
              Project Dashboard
            </div>
            <div style={{ fontSize: "0.85rem", color: "#9e9e9e", marginBottom: "28px" }}>
              Enter the password to view this project
            </div>
            <Form onSubmit={(e) => { e.preventDefault(); submitPassword(); }}>
              <input
                ref={passwordref}
                type="password"
                placeholder="Password"
                style={{
                  width: "100%", padding: "10px 14px",
                  border: "1px solid #e0e0e0", borderRadius: "9px",
                  fontSize: "0.92rem", outline: "none",
                  marginBottom: "12px",
                  boxSizing: "border-box",
                }}
              />
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%", padding: "11px",
                  background: "linear-gradient(135deg, #1a73e8 0%, #1a237e 100%)",
                  border: "none", borderRadius: "9px",
                  color: "#fff", fontWeight: 700, fontSize: "0.95rem",
                  cursor: loading ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                }}
              >
                {loading ? (
                  <span style={{
                    width: "16px", height: "16px",
                    border: "2px solid rgba(255,255,255,0.4)",
                    borderTopColor: "#fff",
                    borderRadius: "50%",
                    animation: "spin 0.75s linear infinite",
                    display: "inline-block",
                  }} />
                ) : "Unlock"}
              </button>
            </Form>
            {showError && (
              <div style={{
                marginTop: "14px", padding: "10px 14px",
                background: "#ffebee", borderRadius: "8px",
                color: "#c62828", fontSize: "0.82rem", fontWeight: 600,
              }}>
                Project not found or wrong password
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // ── Main dashboard ────────────────────────────────────────────────
  if (!dataLoaded) return null;

  return (
    <>
      <style>{spin}</style>
      <FreelancerserplaceNavbar showLinks={false} showButtons={false} />

      <div style={{ background: "#f5f6fa", minHeight: "calc(100vh - 80px)", padding: "24px 20px" }}>

        {/* Project header */}
        <div style={{
          background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
          borderRadius: "14px",
          padding: "22px 28px",
          marginBottom: "20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          boxShadow: "0 4px 20px rgba(26,115,232,0.25)",
          flexWrap: "wrap", gap: "12px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div style={{
              width: "44px", height: "44px", borderRadius: "11px",
              background: "rgba(255,255,255,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.1rem", color: "#fff", flexShrink: 0,
            }}>
              <FontAwesomeIcon icon={faDiagramProject} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: "1.2rem", color: "#fff", lineHeight: 1.2 }}>
                {dashboardData.title}
              </div>
              <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.65)", marginTop: "3px" }}>
                Customer Project Dashboard
              </div>
            </div>
          </div>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            padding: "9px 18px",
            background: onTrack ? "#43a047" : "#e53935",
            borderRadius: "24px",
            color: "#fff", fontSize: "0.88rem", fontWeight: 800,
            boxShadow: onTrack
              ? "0 4px 14px rgba(67,160,71,0.55)"
              : "0 4px 14px rgba(229,57,53,0.55)",
            letterSpacing: "0.02em",
          }}>
            {onTrack ? "✓ On Track" : "⚠ Behind Schedule"}
          </div>
        </div>

        {/* Metric cards */}
        <div style={{ display: "flex", gap: "14px", marginBottom: "20px", flexWrap: "wrap" }}>
          <MetricCard icon={faHourglassStart} label="Start"          value={dashboardData.start || "—"} color="#2e7d32" bg="#e8f5e9" />
          <MetricCard icon={faHourglassEnd}   label="End"            value={dashboardData.end   || "—"} color="#e65100" bg="#fff3e0" />
          <MetricCard icon={faClock}          label="Planned Hours"  value={(dashboardData.max_hours || 0) + " h"} color="#1a73e8" bg="#e8f0fe" />
          <MetricCard icon={faCheckCircle}    label="Done"           value={donePercentage + " %"} color={donePercentage >= 75 ? "#2e7d32" : donePercentage >= 40 ? "#e65100" : "#1a73e8"} bg={donePercentage >= 75 ? "#e8f5e9" : donePercentage >= 40 ? "#fff3e0" : "#e8f0fe"} />
        </div>

        {/* Comments + chart + tags row */}
        <div style={{ display: "flex", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>

          {/* Comments */}
          <div style={{
            flex: 2, minWidth: "280px",
            background: "#fff", borderRadius: "12px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            overflow: "hidden",
          }}>
            <div style={{
              padding: "13px 16px 11px",
              borderBottom: "1px solid #f0f0f0",
              display: "flex", alignItems: "center", gap: "8px",
            }}>
              <FontAwesomeIcon icon={faComment} style={{ color: "#1a73e8", fontSize: "0.85rem" }} />
              <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#212121" }}>Comments</span>
              {dashboardData.comments?.length > 0 && (
                <span style={{
                  marginLeft: "auto", background: "#e8f0fe", color: "#1a73e8",
                  borderRadius: "20px", fontSize: "0.68rem", fontWeight: 700, padding: "1px 8px",
                }}>{dashboardData.comments.length}</span>
              )}
            </div>
            <div style={{ padding: "12px 16px", maxHeight: "280px", overflowY: "auto" }}>
              {dashboardData.comments?.length > 0
                ? dashboardData.comments.map((comment, index) => (
                    <Fragment key={index}>
                      <div style={{ fontSize: "0.68rem", color: "#bdbdbd", fontWeight: 600, marginBottom: "4px", marginTop: index > 0 ? "12px" : 0 }}>
                        {comment.date}
                      </div>
                      <ProjectCommentTimelineItem comment={comment} showDropdown={false} />
                    </Fragment>
                  ))
                : <div style={{ textAlign: "center", padding: "32px 0", color: "#bdbdbd", fontSize: "0.85rem" }}>No comments yet</div>
              }
            </div>
          </div>

          {/* Right column: tags + chart */}
          <div style={{ flex: 1, minWidth: "220px", display: "flex", flexDirection: "column", gap: "16px" }}>

            {/* Tags */}
            {dashboardData.tags?.length > 0 && (
              <div style={{
                background: "#fff", borderRadius: "12px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                padding: "14px 16px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "10px" }}>
                  <FontAwesomeIcon icon={faTag} style={{ color: "#9e9e9e", fontSize: "0.78rem" }} />
                  <span style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#9e9e9e" }}>Tags</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {dashboardData.tags.map((tag, i) => (
                    <span key={i} style={{
                      background: "#e8f0fe", color: "#1a73e8",
                      borderRadius: "20px", fontSize: "0.75rem", fontWeight: 600,
                      padding: "3px 10px",
                    }}>{typeof tag === "string" ? tag : tag.name || tag.value || JSON.stringify(tag)}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Doughnut chart */}
            {doughnutData && (
              <div style={{
                background: "#fff", borderRadius: "12px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                padding: "14px 16px", flex: 1,
              }}>
                <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#9e9e9e", marginBottom: "12px" }}>
                  Ticket Overview
                </div>
                <div style={{ position: "relative", height: "130px" }}>
                  <Doughnut
                    data={doughnutData}
                    options={{ responsive: true, maintainAspectRatio: false, cutout: "68%", plugins: { legend: { display: false } } }}
                  />
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", marginTop: "10px" }}>
                  {[
                    { name: "Todo",     color: "#1a73e8", count: todo.length },
                    { name: "Progress", color: "#00acc1", count: inProgress.length },
                    { name: "Done",     color: "#2e7d32", count: done.length },
                    { name: "Hold",     color: "#9e9e9e", count: onHold.length },
                    { name: "Canceled", color: "#e53935", count: canceled.length },
                  ].filter((i) => i.count > 0).map(({ name, color, count }) => (
                    <div key={name} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.7rem", color: "#616161" }}>
                      <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 }} />
                      {name} {count}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Ticket columns */}
        <div style={{
          background: "#fff", borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          marginBottom: "20px", overflow: "hidden",
        }}>
          <div style={{
            padding: "13px 16px 11px",
            borderBottom: "1px solid #f0f0f0",
            display: "flex", alignItems: "center", gap: "8px",
          }}>
            <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#212121" }}>Tickets</span>
            <span style={{ fontSize: "0.78rem", color: "#9e9e9e" }}>— {total} total</span>
            {urgentOpen > 0 && (
              <span style={{
                marginLeft: "8px", display: "inline-flex", alignItems: "center", gap: "4px",
                background: "#fff3e0", color: "#e65100",
                borderRadius: "20px", fontSize: "0.68rem", fontWeight: 700, padding: "2px 9px",
              }}>
                <FontAwesomeIcon icon={faFire} style={{ fontSize: "0.6rem" }} />
                {urgentOpen} high priority open
              </span>
            )}
          </div>
          <div style={{ padding: "12px", display: "flex", gap: "10px", overflowX: "auto" }}>
            <TicketColumn label="Todo"        actions={todo}       status="Created" />
            <TicketColumn label="In Progress" actions={inProgress} status="In_Progress" />
            <TicketColumn label="On Hold"     actions={onHold}     status="On_Hold" />
            <TicketColumn label="Done"        actions={done}       status="Done" />
            <TicketColumn label="Canceled"    actions={canceled}   status="Canceled" />
          </div>
        </div>

        {/* Working hours + time status */}
        {(barData || dashboardData.total_time != null) && (
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>

            {barData && (
              <div style={{
                flex: 2, minWidth: "280px",
                background: "#fff", borderRadius: "12px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                padding: "16px",
              }}>
                <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#212121", marginBottom: "14px" }}>
                  Working Hours by Task
                </div>
                <div style={{ height: "180px" }}>
                  <Bar data={barData} options={barOptions} />
                </div>
              </div>
            )}

            <div style={{ flex: 1, minWidth: "200px", display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{
                background: "#fff", borderRadius: "12px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                padding: "16px",
              }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9e9e9e", marginBottom: "6px" }}>Time Used</div>
                <div style={{ height: "6px", background: "#ebebeb", borderRadius: "3px", overflow: "hidden", marginBottom: "8px" }}>
                  <div style={{
                    height: "100%",
                    width: `${Math.min(100, timePercentage).toFixed(1)}%`,
                    background: onTrack ? "linear-gradient(90deg, #1a73e8, #2e7d32)" : "linear-gradient(90deg, #e65100, #c62828)",
                    borderRadius: "3px", transition: "width 0.5s",
                  }} />
                </div>
                <div style={{ fontSize: "1.5rem", fontWeight: 800, color: onTrack ? "#2e7d32" : "#c62828" }}>
                  {timePercentage.toFixed(0)}%
                </div>
                <div style={{ fontSize: "0.78rem", color: "#9e9e9e" }}>of planned hours used</div>
              </div>

              <div style={{
                background: onTrack ? "#e8f5e9" : "#ffebee",
                borderRadius: "12px",
                padding: "16px",
                border: `1px solid ${onTrack ? "#c8e6c9" : "#ffcdd2"}`,
              }}>
                <div style={{ fontWeight: 800, fontSize: "1rem", color: onTrack ? "#2e7d32" : "#c62828", marginBottom: "4px" }}>
                  {onTrack ? "On Track" : "Behind Schedule"}
                </div>
                <div style={{ fontSize: "0.78rem", color: onTrack ? "#388e3c" : "#c62828" }}>
                  {donePercentage}% done · {timePercentage.toFixed(0)}% time used
                </div>
              </div>

              <div style={{
                background: "#fff", borderRadius: "12px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                padding: "16px",
              }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9e9e9e", marginBottom: "4px" }}>Logged Hours</div>
                <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#212121" }}>
                  {dashboardData.total_time ?? 0} h
                </div>
                <div style={{ fontSize: "0.78rem", color: "#9e9e9e" }}>
                  of {dashboardData.max_hours ?? 0} h planned
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CustomerDashboard;
